import React, { useState, useEffect, useCallback } from 'react';
import { activityLogsAPI } from '../../services/api';
import Pagination from '../../components/common/Pagination';
import Spinner from '../../components/common/Spinner';
import toast from 'react-hot-toast';

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' }) : '—';

const METHOD_COLORS = {
  GET:    'bg-blue-100 text-blue-700',
  POST:   'bg-green-100 text-green-700',
  PUT:    'bg-yellow-100 text-yellow-700',
  PATCH:  'bg-orange-100 text-orange-700',
  DELETE: 'bg-red-100 text-red-700',
};

const statusColor = (code) => {
  if (!code) return 'bg-gray-100 text-gray-500';
  if (code < 300) return 'bg-green-100 text-green-700';
  if (code < 400) return 'bg-blue-100 text-blue-700';
  if (code < 500) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};

const MethodBadge = ({ method }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${METHOD_COLORS[method] || 'bg-gray-100 text-gray-600'}`}>
    {method || '—'}
  </span>
);

const StatusBadge = ({ code }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusColor(code)}`}>
    {code || '—'}
  </span>
);

const JsonView = ({ data }) => {
  if (!data) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
};

const MODULES = [
  'users', 'customers', 'plans', 'resellers', 'subscriptions', 'payments',
  'roles', 'activation_codes', 'activity_logs', 'settings', 'dashboard', 'usage'
];

const INIT_FILTERS = { method: '', action: '', module: '', statusGroup: '', dateFrom: '', dateTo: '', search: '' };

// ── Component ─────────────────────────────────────────────────────────────────
const ActivityLogs = () => {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [pagination, setPagination] = useState(null);
  const [page, setPage]             = useState(1);
  const [filters, setFilters]       = useState(INIT_FILTERS);
  const [expanded, setExpanded]     = useState(null);
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await activityLogsAPI.getAll(params);
      setLogs(res.data.data);
      setPagination(res.data.pagination);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const applyFilter = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); };
  const clearFilters = () => { setFilters(INIT_FILTERS); setPage(1); };

  const toggleExpand = async (log) => {
    if (expanded === log._id) { setExpanded(null); setDetail(null); return; }
    setExpanded(log._id);
    setDetailLoading(true);
    try {
      const res = await activityLogsAPI.getOne(log._id);
      setDetail(res.data.data);
    } catch { setDetail(log); }
    finally { setDetailLoading(false); }
  };

  const hasFilter = Object.values(filters).some(v => v !== '');

  return (
    <div className="space-y-4">

      {/* ── Filter bar ── */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap gap-3">
          <select value={filters.method} onChange={e => applyFilter('method', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Methods</option>
            {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m}>{m}</option>)}
          </select>

          <select value={filters.action} onChange={e => applyFilter('action', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Actions</option>
            {['create','read','update','delete'].map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          <select value={filters.module} onChange={e => applyFilter('module', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Modules</option>
            {MODULES.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
          </select>

          <select value={filters.statusGroup} onChange={e => applyFilter('statusGroup', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Status</option>
            <option value="200">2xx Success</option>
            <option value="400">4xx Client Error</option>
            <option value="500">5xx Server Error</option>
          </select>

          <input type="date" value={filters.dateFrom} onChange={e => applyFilter('dateFrom', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />

          <input type="date" value={filters.dateTo} onChange={e => applyFilter('dateTo', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />

          <input type="text" placeholder="Search URL…" value={filters.search}
            onChange={e => applyFilter('search', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[180px]" />

          {hasFilter && (
            <button onClick={clearFilters}
              className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
              Clear
            </button>
          )}

          {pagination?.total > 0 && (
            <span className="ml-auto self-center text-xs text-gray-400">
              {pagination.total.toLocaleString()} record{pagination.total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Spinner /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                    <th className="px-4 py-3 font-medium">Performed By</th>
                    <th className="px-4 py-3 font-medium">Method</th>
                    <th className="px-4 py-3 font-medium">URL</th>
                    <th className="px-4 py-3 font-medium">Module</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Duration</th>
                    <th className="px-4 py-3 font-medium">IP</th>
                    <th className="px-4 py-3 font-medium">Time</th>
                    <th className="px-4 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center text-gray-400">
                        <p className="text-3xl mb-2">📋</p>
                        <p>No activity logs found</p>
                        {hasFilter && (
                          <button onClick={clearFilters} className="mt-2 text-indigo-600 text-xs hover:underline">Clear filters</button>
                        )}
                      </td>
                    </tr>
                  ) : logs.map(log => (
                    <React.Fragment key={log._id}>
                      <tr className={`hover:bg-gray-50 transition-colors ${expanded === log._id ? 'bg-indigo-50' : ''}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 text-xs">{log.user?.name || 'System'}</p>
                          <p className="text-gray-400 text-xs">{log.user?.email}</p>
                        </td>
                        <td className="px-4 py-3"><MethodBadge method={log.method} /></td>
                        <td className="px-4 py-3 max-w-[240px]">
                          <p className="font-mono text-xs text-gray-700 truncate" title={log.url}>{log.url || log.description}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="capitalize text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
                            {(log.module || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3"><StatusBadge code={log.statusCode} /></td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {log.duration != null ? `${log.duration}ms` : '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-400">{log.ipAddress || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDateTime(log.createdAt)}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => toggleExpand(log)}
                            className="text-indigo-500 hover:text-indigo-700 text-xs font-medium">
                            {expanded === log._id ? '▲ Hide' : '▼ Detail'}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded detail */}
                      {expanded === log._id && (
                        <tr className="bg-indigo-50">
                          <td colSpan={9} className="px-6 py-5">
                            {detailLoading ? (
                              <div className="flex justify-center py-4"><Spinner /></div>
                            ) : detail ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Meta */}
                                <div className="space-y-3">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Performed By</p>
                                    <p className="text-sm font-medium">{detail.user?.name || 'System'}</p>
                                    <p className="text-xs text-gray-400">{detail.user?.email}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Full URL</p>
                                    <p className="font-mono text-xs text-gray-700 break-all">{detail.url || detail.description}</p>
                                  </div>
                                  <div className="flex gap-6">
                                    <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Status</p><StatusBadge code={detail.statusCode} /></div>
                                    <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Duration</p><p className="text-sm">{detail.duration != null ? `${detail.duration}ms` : '—'}</p></div>
                                    <div><p className="text-xs font-semibold text-gray-500 uppercase mb-1">IP</p><p className="font-mono text-xs">{detail.ipAddress || '—'}</p></div>
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">User Agent</p>
                                    <p className="text-xs text-gray-500 break-all">{detail.userAgent || '—'}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Timestamp</p>
                                    <p className="text-xs">{formatDateTime(detail.createdAt)}</p>
                                  </div>
                                </div>
                                {/* Payloads */}
                                <div className="space-y-3">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Request Body</p>
                                    <JsonView data={detail.requestBody} />
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Response Body</p>
                                    <JsonView data={detail.responseBody} />
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination?.pages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  Page {pagination.page} of {pagination.pages} · {pagination.total.toLocaleString()} total
                </p>
                <Pagination pagination={pagination} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
